import React, { useState, useEffect } from "react";
import { COLORS } from "../../theme/colors";
import { webhookApi } from "../../api/client";
import { Button, Badge } from "../../components/ui";

const STATUS_DOT = {
  received: COLORS.cyan,
  processing: COLORS.orange,
  completed: COLORS.green,
  failed: COLORS.pink,
};

export default function WebhookEventLog({ webhook, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await webhookApi.events(webhook.id);
      if (res.success) setEvents(res.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [webhook.id]);

  const filtered = filter === "all" ? events : events.filter(e => e.status === filter);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "16px 20px", borderBottom: `1px solid ${COLORS.border}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button variant="ghost" size="sm" onClick={onClose} style={{ fontSize: 16, padding: "2px 6px" }}>
            {"\u2190"}
          </Button>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Event Log</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted }}>{webhook.name} - {events.length} events</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {["all", "completed", "failed"].map(f => (
            <Button key={f} variant={filter === f ? "primary" : "secondary"} size="sm"
              onClick={() => setFilter(f)}
              style={filter === f
                ? { background: COLORS.accentSoft, color: COLORS.accentText, border: `1px solid ${COLORS.accent}40` }
                : { textTransform: "capitalize" }
              }>
              {f}
            </Button>
          ))}
          <Button variant="secondary" size="sm" onClick={load}>{"\u21BB"} Refresh</Button>
        </div>
      </div>

      {/* Events */}
      <div className="panel-scroll" style={{ flex: 1, overflowY: "auto", padding: "8px 12px" }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>Loading events...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 12 }}>No events found</div>
        ) : filtered.map(event => (
          <div key={event.id} style={{
            marginBottom: 4, borderRadius: 10, border: `1px solid ${COLORS.border}`,
            background: expanded === event.id ? COLORS.surfaceHover : COLORS.surface,
            transition: "all 0.15s",
          }}>
            {/* Row summary */}
            <div onClick={() => setExpanded(expanded === event.id ? null : event.id)} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
              cursor: "pointer",
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: STATUS_DOT[event.status] || COLORS.textDim,
                boxShadow: `0 0 4px ${(STATUS_DOT[event.status] || COLORS.textDim)}66`,
                flexShrink: 0,
              }} />
              <span style={{ fontSize: 11, color: COLORS.text, fontWeight: 600, textTransform: "capitalize", minWidth: 70 }}>
                {event.status}
              </span>
              <span style={{ fontSize: 11, color: COLORS.textMuted, flex: 1 }}>
                {event.received_at ? new Date(event.received_at).toLocaleString() : "-"}
              </span>
              <span style={{ fontSize: 11, color: COLORS.textMuted, minWidth: 80, textAlign: "right" }}>
                {event.recipient || "-"}
              </span>
              <span style={{ fontSize: 11, color: COLORS.textDim, minWidth: 50, textAlign: "right" }}>
                {event.latency_ms != null ? `${event.latency_ms}ms` : "-"}
              </span>
              <span style={{ fontSize: 10, color: COLORS.textDim, transform: expanded === event.id ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}>
                {"\u25BE"}
              </span>
            </div>

            {/* Expanded detail */}
            {expanded === event.id && (
              <div style={{ padding: "0 14px 14px", borderTop: `1px solid ${COLORS.border}` }}>
                {event.error_message && (
                  <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 6, background: `${COLORS.pink}10`, border: `1px solid ${COLORS.pink}25` }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.pink, marginBottom: 4 }}>Error</div>
                    <div style={{ fontSize: 11, color: COLORS.text }}>{event.error_message}</div>
                  </div>
                )}
                {event.message_body && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, marginBottom: 4 }}>Message</div>
                    <div style={{ fontSize: 11, color: COLORS.text, padding: "6px 8px", borderRadius: 6, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
                      {event.message_body}
                    </div>
                  </div>
                )}
                {event.payload && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, marginBottom: 4 }}>Payload</div>
                    <pre style={{
                      fontSize: 10, color: COLORS.textMuted, padding: "8px 10px", borderRadius: 6,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`, overflow: "auto",
                      maxHeight: 200, margin: 0, whiteSpace: "pre-wrap",
                    }}>{JSON.stringify(event.payload, null, 2)}</pre>
                  </div>
                )}
                {event.action_result && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, marginBottom: 4 }}>Action Result</div>
                    <pre style={{
                      fontSize: 10, color: COLORS.textMuted, padding: "8px 10px", borderRadius: 6,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`, overflow: "auto",
                      maxHeight: 200, margin: 0, whiteSpace: "pre-wrap",
                    }}>{JSON.stringify(event.action_result, null, 2)}</pre>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
